/**
 * 承認一覧の絞り込み — 種別・状態・操作の 3 段の切替と、案件の選択。
 *
 * 絞り込みの状態は画面（`ApprovalsPage`）が持ち、ここは選択肢と変更の通知だけを描く。
 * **承認の種別は提案が 0 件でも常に出す。**
 * 選択肢は契約の `APPROVAL_KINDS` から引く — 行に現れた種別から作ると、提案の無い種別が
 * 画面から消え、「その種別は承認の対象ではない」と読める。
 *
 * **状態と操作の選択肢も契約の allowlist から引く**（`APPROVAL_ROW_STATES` /
 * `APPROVAL_ROW_OPERATIONS`）。一覧が返す値と選択肢が黙ってずれないようにする。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認一覧の絞り込み（種別の切替と案件の選択、畳んだ状態・操作の切替）を描く。
 * @graph-connects none
 */
import {
  APPROVAL_KINDS,
  APPROVAL_ROW_OPERATIONS,
  APPROVAL_ROW_STATES,
} from '@cortex/domain-spec-console';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useTranslation } from 'react-i18next';

import type { ApprovalFilter } from '../approvals-selection.js';

/** `ApprovalFilterBar` の props。 @graph-connects none */
export interface ApprovalFilterBarProps {
  filter: ApprovalFilter;
  /** 案件の絞り込みの選択肢（行に現れる案件番号）。 */
  pjOptions: readonly string[];
  onChange: (filter: ApprovalFilter) => void;
}

/** `ToggleGroup` の props。 @graph-connects none */
interface ToggleGroupProps {
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onSelect: (value: string) => void;
}

/**
 * 切替 1 段。先頭に「すべて」（空文字）を置き、選んでいるものだけ塗る。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 絞り込みの切替 1 段を、すべて＋選択肢のボタンの並びで描く。
 * @graph-connects none
 */
function ToggleGroup({ label, value, options, onSelect }: ToggleGroupProps) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={label}>
      <span className="w-12 text-xs text-[var(--color-text-muted)]">{label}</span>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          data-filter-option={option.value}
          className={
            value === option.value
              ? 'rounded-sm border border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)] px-2 py-0.5 text-xs text-[var(--color-bg-base)]'
              : 'rounded-sm border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]'
          }
          onClick={() => {
            onSelect(option.value);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * 絞り込み。種別・状態・操作は切替、案件は選択で絞る。どれも「すべて」（空文字）を持つ。
 * 種別と案件を常に見せ、状態（既定 未承認）と操作は「ほかの絞り込み」に畳む（既定は閉じる）。
 * 状態は既定のままで足り、操作は行の札で読めるためである。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認一覧の絞り込み（種別の切替と案件の選択、畳んだ状態・操作の切替）を描く。
 * @graph-connects none
 */
export function ApprovalFilterBar({ filter, pjOptions, onChange }: ApprovalFilterBarProps) {
  const { t } = useTranslation();
  const all = { value: '', label: t('approvals.filterAll') };

  return (
    <div className="flex flex-col gap-2">
      <ToggleGroup
        label={t('approvals.filterKind')}
        value={filter.kind}
        options={[
          all,
          ...APPROVAL_KINDS.map((kind) => ({ value: kind, label: t(`approvals.kind.${kind}`) })),
        ]}
        onSelect={(value) => {
          onChange({ ...filter, kind: value as ApprovalFilter['kind'] });
        }}
      />
      <div className="max-w-xs">
        <Select
          size="sm"
          fullWidth
          label={t('approvals.filterPj')}
          value={filter.pj}
          options={[all, ...pjOptions.map((pjNumber) => ({ value: pjNumber, label: pjNumber }))]}
          onChange={(event) => {
            onChange({ ...filter, pj: event.target.value });
          }}
        />
      </div>
      <details data-approval-more-filters>
        <summary className="cursor-pointer text-xs text-[var(--color-text-muted)]">
          {t('approvals.moreFilters')}
        </summary>
        <div className="mt-2 flex flex-col gap-2">
          <ToggleGroup
            label={t('approvals.filterState')}
            value={filter.state}
            options={[
              all,
              ...APPROVAL_ROW_STATES.map((state) => ({
                value: state,
                label: t(`approvals.state.${state}`),
              })),
            ]}
            onSelect={(value) => {
              onChange({ ...filter, state: value as ApprovalFilter['state'] });
            }}
          />
          <ToggleGroup
            label={t('approvals.filterOperation')}
            value={filter.operation}
            options={[
              all,
              ...APPROVAL_ROW_OPERATIONS.map((operation) => ({
                value: operation,
                label: t(`approvals.operation.${operation}`),
              })),
            ]}
            onSelect={(value) => {
              onChange({ ...filter, operation: value as ApprovalFilter['operation'] });
            }}
          />
        </div>
      </details>
    </div>
  );
}
